import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getAllCustomers, deleteCustomer } from '../services/customerService';
import { getCustomerDropLotLocations } from '../services/dropLotLocationService';
import {
    Box,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    CardActions,
    Chip,
    Divider,
    Tabs,
    Tab,
    Tooltip,
    Stack,
    TextField,
    InputAdornment,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Badge,
    Container,
    useTheme,
    useMediaQuery,
    Fab,
    Collapse,
    Avatar,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import MapIcon from '@mui/icons-material/Map';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import FilterListIcon from '@mui/icons-material/FilterList';
import PhoneIcon from '@mui/icons-material/Phone';

import PinDropIcon from '@mui/icons-material/PinDrop';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import NotesIcon from '@mui/icons-material/Notes';
// Import the form component
import CustomerForm from './CustomerForm';
// Import the map component
import MapComponent from './MapComponent';
// Import the new customer detail view
import CustomerDetailView from './CustomerDetailView';
// Import skeleton
import TableSkeleton from './skeleton/TableSkeleton';
import EmptyState from './common/EmptyState';
import ErrorState from './common/ErrorState';
import { useAuditTracking } from '../hooks/useAuditTracking';
import { useAuth } from '../context/AuthContext'; // Import useAuth

function CustomerList() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    // State for managing the form dialog
    const [openForm, setOpenForm] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    // State for view mode (table, card, map)
    const [viewMode, setViewMode] = useState('table');
    // State for search
    const [searchTerm, setSearchTerm] = useState(localStorage.getItem('customers.search') || '');
    // State for filtered customers
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    // State for delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    // New state for customer detail view
    const [detailViewOpen, setDetailViewOpen] = useState(false);
    const [detailCustomer, setDetailCustomer] = useState(null);
    // State for drop lot counts
    const [dropLotCounts, setDropLotCounts] = useState({});
    // State for filters
    const [showFilters, setShowFilters] = useState(false);
    const [statusFilter, setStatusFilter] = useState(localStorage.getItem('customers.filter') || 'all');
    const [orderBy, setOrderBy] = useState(localStorage.getItem('customers.orderBy') || 'name');
    const [order, setOrder] = useState(localStorage.getItem('customers.order') || 'asc');
    // State for notes dialog
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);
    const [selectedCustomerNotes, setSelectedCustomerNotes] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    // Add audit tracking
    const {
        trackClick,
        trackForm,
        trackSearchAction,
        trackFilterAction,
        trackModal,
        trackCRUD
    } = useAuditTracking();

    const { user } = useAuth(); // Get user from context

    const abortRef = useRef(null);
    const fetchCustomers = useCallback(async (signal) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllCustomers();
            if (signal?.aborted) return;
            setCustomers(response.data);
            setFilteredCustomers(response.data);
            // Fetch drop lot counts for each customer
            await fetchDropLotCounts(response.data);
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            setError('Failed to fetch customers. Please try again later.');
            console.error('Fetch error:', err);
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, []);

    const fetchDropLotCounts = async (customerList) => {
        const counts = {};
        try {
            await Promise.all(
                customerList.map(async (customer) => {
                    try {
                        const dropLotsResponse = await getCustomerDropLotLocations(customer._id);
                        counts[customer._id] = dropLotsResponse.data?.data?.length || 0;
                    } catch (err) {
                        // If user doesn't have access to customer's drop lots, set count to 0
                        counts[customer._id] = 0;
                    }
                })
            );
            setDropLotCounts(counts);
        } catch (err) {
            console.error('Error fetching drop lot counts:', err);
        }
    };

    useEffect(() => {
        console.log('[CustomerList] useEffect triggered - component mounted');
        console.log('[CustomerList] Current path:', window.location.pathname);
        const controller = new AbortController();
        abortRef.current = controller;
        fetchCustomers(controller.signal);
        return () => {
            try { controller.abort(); } catch (_) {}
        };
    }, [fetchCustomers]);

    // Filter customers when search term changes
    useEffect(() => {
        let filtered = [...customers];

        // Apply status filter (active customers with drop lots vs without)
        if (statusFilter === 'with_drop_lots') {
            filtered = filtered.filter(customer => dropLotCounts[customer._id] > 0);
        } else if (statusFilter === 'without_drop_lots') {
            filtered = filtered.filter(customer => !dropLotCounts[customer._id] || dropLotCounts[customer._id] === 0);
        }

        // Apply search filter
        if (searchTerm.trim()) {
            const lowercasedSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(customer => 
                customer.name.toLowerCase().includes(lowercasedSearch) ||
                (customer.contactPerson && customer.contactPerson.toLowerCase().includes(lowercasedSearch)) ||
                (customer.address && customer.address.city && customer.address.city.toLowerCase().includes(lowercasedSearch))
            );
        }
        
        // Sort
        const comparator = (a, b) => {
            let aVal, bVal;
            switch (orderBy) {
                case 'name':
                    aVal = a.name?.toLowerCase() || '';
                    bVal = b.name?.toLowerCase() || '';
                    break;
                case 'city':
                    aVal = a.address?.city?.toLowerCase() || '';
                    bVal = b.address?.city?.toLowerCase() || '';
                    break;
                case 'droplots':
                    aVal = dropLotCounts[a._id] || 0;
                    bVal = dropLotCounts[b._id] || 0;
                    break;
                default:
                    aVal = a[orderBy];
                    bVal = b[orderBy];
            }
            if (aVal < bVal) return order === 'asc' ? -1 : 1;
            if (aVal > bVal) return order === 'asc' ? 1 : -1;
            return 0;
        };
        filtered.sort(comparator);

        setFilteredCustomers(filtered);
    }, [searchTerm, customers, statusFilter, dropLotCounts, orderBy, order]);

    // Persist settings
    useEffect(() => {
        localStorage.setItem('customers.search', searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        localStorage.setItem('customers.filter', statusFilter);
    }, [statusFilter]);

    useEffect(() => {
        localStorage.setItem('customers.orderBy', orderBy);
    }, [orderBy]);

    useEffect(() => {
        localStorage.setItem('customers.order', order);
    }, [order]);

    // Open delete confirmation dialog
    const handleOpenDeleteDialog = (customer) => {
        setCustomerToDelete(customer);
        setDeleteDialogOpen(true);
        trackModal('open', 'Delete Customer Dialog', {
            customerId: customer._id,
            customerName: customer.name
        });
    };

    // Close delete confirmation dialog
    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setCustomerToDelete(null);
        setDeleteLoading(false);
    };

    // Confirm and execute delete
    const handleConfirmDelete = async () => {
        if (!customerToDelete) return;
        
        setDeleteLoading(true);
        try {
            await deleteCustomer(customerToDelete._id);
            // Refetch or filter list locally
            setCustomers(customers.filter(c => c._id !== customerToDelete._id));
            setFilteredCustomers(filteredCustomers.filter(c => c._id !== customerToDelete._id));
            
            // Track successful deletion
            trackForm('delete_customer', {
                customerId: customerToDelete._id,
                customerName: customerToDelete.name
            }, true);
            
            handleCloseDeleteDialog();
        } catch (err) {
            setError('Failed to delete customer.');
            console.error('Delete error:', err);
            
            // Track failed deletion
            trackForm('delete_customer', {
                customerId: customerToDelete._id,
                customerName: customerToDelete.name,
                error: err.message
            }, false);
            
            setDeleteLoading(false);
        }
    };

    // Implement Add/Edit handlers to control the dialog
    const handleAdd = () => {
        setSelectedCustomer(null); // Clear selected customer for Add mode
        setOpenForm(true);
        trackClick('Add Customer', {
            source: 'customer_list',
            action: 'open_form'
        });
    };

    const handleEdit = (customer) => {
        setSelectedCustomer(customer); // Set selected customer for Edit mode
        setOpenForm(true);
        trackCRUD('update', 'customer', customer._id, {
            customerName: customer.name,
            source: 'customer_list'
        });
    };

    const handleCloseForm = () => {
        setOpenForm(false);
        setSelectedCustomer(null); // Clear selection on close
    };

    const handleSave = async () => {
        setRefreshing(true);
        setError(null);
        
        try {
            await fetchCustomers(); // Refetch the list after saving
            
            // Show success message
            const message = selectedCustomer 
                ? 'Customer updated successfully!' 
                : 'Customer created successfully and added to your list!';
            setSuccessMessage(message);
            
            // Clear success message after delay
            setTimeout(() => {
                setSuccessMessage('');
            }, 4000);
            
        } catch (err) {
            console.error('Error refreshing customers:', err);
            setError('Customer saved but failed to refresh list. Please reload the page.');
        } finally {
            setRefreshing(false);
            setOpenForm(false);
            setSelectedCustomer(null);
        }
        
        // Track form save (actual tracking happens in the form component)
        trackForm('customer_save', {
            isNewCustomer: !selectedCustomer,
            customerId: selectedCustomer?._id
        }, true);
    };

    // Handle view mode change
    const handleViewModeChange = (event, newValue) => {
        if (newValue !== null) {
            setViewMode(newValue);
            trackClick('View Mode Change', {
                fromMode: viewMode,
                toMode: newValue,
                customerCount: filteredCustomers.length
            });
        }
    };

    // Enhanced search with tracking
    const handleSearchChange = (event) => {
        const searchValue = event.target.value;
        setSearchTerm(searchValue);
        
        // Track search action with debouncing
        if (searchValue.length >= 3 || searchValue.length === 0) {
            trackSearchAction(searchValue, filteredCustomers.length, {
                searchType: 'customer_search',
                resultsCount: filteredCustomers.length
            });
        }
    };

    // Enhanced filter tracking
    const handleStatusFilterChange = (newFilter) => {
        setStatusFilter(newFilter);
        trackFilterAction({
            filterType: 'customer_status',
            filterValue: newFilter,
            resultCount: filteredCustomers.length
        });
    };

    // Handle marker click on map
    const handleMarkerClick = (customer) => {
        setSelectedCustomer(customer);
    };

    // Format address
    const formatAddress = (address) => {
        if (!address) return 'No address';
        const parts = [address.street, address.city, address.state, address.zipCode].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : 'No address';
    };

    // Handle view details
    const handleViewDetails = (customer) => {
        setDetailCustomer(customer);
        setDetailViewOpen(true);
        trackCRUD('read', 'customer', customer._id, {
            customerName: customer.name,
            source: 'customer_list'
        });
    };

    const handleCloseDetailView = () => {
        setDetailViewOpen(false);
        setDetailCustomer(null);
    };

    // Handle notes dialog
    const handleViewNotes = (customer) => {
        setSelectedCustomerNotes(customer);
        setNotesDialogOpen(true);
        trackModal('open', 'Customer Notes Dialog', {
            customerId: customer._id,
            customerName: customer.name,
            hasNotes: !!customer.notes
        });
    };

    const handleCloseNotes = () => {
        setNotesDialogOpen(false);
        setSelectedCustomerNotes(null);
    };

    // Calculate stats
    const totalCustomers = customers.length;
    const customersWithDropLots = Object.values(dropLotCounts).filter(count => count > 0).length;
    const totalDropLots = Object.values(dropLotCounts).reduce((sum, count) => sum + count, 0);

    if (loading) {
        return (
            <Container maxWidth="xl" disableGutters sx={{ py: 3 }}>
                <TableSkeleton rows={8} showHeader={true} showActions={true} />
            </Container>
        );
    }

    const renderStatsCards = () => (
        <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
                <Card 
                    sx={{ 
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        color: 'white',
                        height: '100%'
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>
                                    Total Customers
                                </Typography>
                                <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700, lineHeight: 1 }}>
                                    {totalCustomers}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                <PeopleIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Card 
                    sx={{ 
                        background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)',
                        color: 'white',
                        height: '100%'
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>
                                    With Drop Lots
                                </Typography>
                                <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700, lineHeight: 1 }}>
                                    {customersWithDropLots}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                <LocationOnIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Card 
                    sx={{ 
                        background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)',
                        color: 'white',
                        height: '100%'
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>
                                    Total Drop Lots
                                </Typography>
                                <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700, lineHeight: 1 }}>
                                    {totalDropLots}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                <PinDropIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Card 
                    sx={{ 
                        background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)',
                        color: 'white',
                        height: '100%'
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>
                                    Avg per Customer
                                </Typography>
                                <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700, lineHeight: 1 }}>
                                    {totalCustomers > 0 ? (totalDropLots / totalCustomers).toFixed(1) : '0'}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                <TrendingUpIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const renderTableView = () => (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
            <TableContainer sx={{ maxHeight: 560 }}>
                <Table stickyHeader>
                    <TableHead sx={{ bgcolor: 'rgba(233, 79, 55, 0.08)' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => handleRequestSort('name')}>Customer</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Contact Person</TableCell>
                            <TableCell sx={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => handleRequestSort('city')}>Location</TableCell>
                            <TableCell sx={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => handleRequestSort('droplots')}>Drop Lots</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCustomers.map((customer) => (
                            <TableRow 
                                key={customer._id} 
                                hover
                                sx={{ 
                                    '&:hover': { bgcolor: 'action.hover' },
                                    cursor: 'pointer'
                                }}
                                onClick={() => handleViewDetails(customer)}
                            >
                                <TableCell>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                                            <BusinessIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={500}>
                                                {customer.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                ID: {customer._id.slice(-6)}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        variant="standard"
                                        value={customer.contactPerson || ''}
                                        placeholder="Add contact"
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setCustomers(prev => prev.map(c => c._id === customer._id ? { ...c, contactPerson: value } : c));
                                        }}
                                        onBlur={async (e) => {
                                            try {
                                                await handleEdit({ ...customer, contactPerson: e.target.value });
                                            } catch {}
                                        }}
                                        fullWidth
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{formatAddress(customer.address)}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={dropLotCounts[customer._id] || 0}
                                        size="small"
                                        color={dropLotCounts[customer._id] > 0 ? 'success' : 'default'}
                                        icon={<LocationOnIcon />}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                        {customer.notes && (
                                            <Tooltip title="View Notes">
                                                <IconButton 
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewNotes(customer);
                                                    }}
                                                >
                                                    <NotesIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="View Details">
                                            <IconButton 
                                                size="small" 
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewDetails(customer);
                                                }}
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {user?.role !== 'company_user' && (
                                            <>
                                                <Tooltip title="Edit Customer">
                                                    <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEdit(customer);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Customer">
                                                    <IconButton 
                                                        size="small" 
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenDeleteDialog(customer);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {filteredCustomers.length === 0 && (
                <Box sx={{ p: 2 }}>
                    <EmptyState
                        icon={BusinessIcon}
                        title={customers.length === 0 ? 'No customers yet' : 'No customers match your search'}
                        subtitle={customers.length === 0 ? 'Start by adding your first customer.' : 'Try clearing filters or changing your search.'}
                        actionLabel={user?.role !== 'company_user' ? 'Add Customer' : undefined}
                        onAction={user?.role !== 'company_user' ? handleAdd : undefined}
                    />
                </Box>
            )}
        </Paper>
    );

    const renderCardView = () => (
        <Grid container spacing={3}>
            {filteredCustomers.map((customer) => (
                <Grid item xs={12} sm={6} lg={4} key={customer._id}>
                    <Card 
                        sx={{ 
                            height: '100%',
                            borderRadius: 3,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: 'pointer',
                            '&:hover': { 
                                transform: 'translateY(-4px)',
                                boxShadow: theme.shadows[8]
                            },
                            position: 'relative',
                            overflow: 'visible'
                        }}
                        onClick={() => handleViewDetails(customer)}
                    >
                        {/* Status Badge */}
                        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
                            <Chip 
                                label={`${dropLotCounts[customer._id] || 0} Drop Lots`}
                                size="small"
                                color={dropLotCounts[customer._id] > 0 ? 'success' : 'default'}
                                sx={{ fontWeight: 600 }}
                            />
                        </Box>

                        <CardContent sx={{ p: 3, pb: 1 }}>
                            <Stack spacing={2}>
                                {/* Header */}
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Avatar 
                                        sx={{ 
                                            bgcolor: 'primary.main',
                                            width: 56,
                                            height: 56,
                                            fontSize: '1.5rem'
                                        }}
                                    >
                                        {customer.name.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography 
                                            variant="h6" 
                                            fontWeight={600}
                                            noWrap
                                            sx={{ pr: 8 }} // Make room for the badge
                                        >
                                            {customer.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Customer ID: {customer._id.slice(-8)}
                                        </Typography>
                                    </Box>
                                </Stack>

                                {/* Contact Info */}
                                <Stack spacing={1}>
                                    {customer.contactPerson && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <BusinessIcon fontSize="small" color="action" />
                                            <Typography variant="body2" noWrap>
                                                {customer.contactPerson}
                                            </Typography>
                                        </Stack>
                                    )}

                                    {customer.phone && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PhoneIcon fontSize="small" color="action" />
                                            <Typography variant="body2">
                                                {customer.phone}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>

                                {/* Address */}
                                <Box>
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {formatAddress(customer.address)}
                                        </Typography>
                                    </Stack>
                                </Box>

                                {/* Drop Lots Progress */}
                                {dropLotCounts[customer._id] > 0 && (
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="caption" color="text.secondary">
                                                Drop Lot Coverage
                                            </Typography>
                                            <Typography variant="caption" fontWeight={600}>
                                                {dropLotCounts[customer._id]} location{dropLotCounts[customer._id] !== 1 ? 's' : ''}
                                            </Typography>
                                        </Stack>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={Math.min((dropLotCounts[customer._id] / 5) * 100, 100)} 
                                            sx={{ height: 6, borderRadius: 3 }}
                                            color="success"
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </CardContent>

                        <CardActions sx={{ px: 3, py: 2, pt: 0 }}>
                            <Button 
                                size="small" 
                                startIcon={<VisibilityIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewDetails(customer);
                                }}
                            >
                                View Details
                            </Button>
                            {user?.role !== 'company_user' && (
                                <>
                                    <Button 
                                        size="small" 
                                        startIcon={<EditIcon />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEdit(customer);
                                        }}
                                    >
                                        Edit
                                    </Button>
                                    <IconButton 
                                        size="small" 
                                        color="error"
                                        sx={{ ml: 'auto' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenDeleteDialog(customer);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </>
                            )}
                            <Button 
                                size="small" 
                                startIcon={<NotesIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewNotes(customer);
                                }}
                            >
                                Notes
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>
            ))}
            {filteredCustomers.length === 0 && (
                <Grid item xs={12}>
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                        <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography
                            variant="h6"
                            color="text.secondary"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                        >
                            {filteredCustomers.length === 0 && customers.length > 0
                                ? "No customers match your search criteria"
                                : user?.role === 'company_user'
                                    ? "No customer access provided yet"
                                    : "Welcome! Start by adding your first customer"
                            }
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3 }}
                        >
                            {filteredCustomers.length === 0 && customers.length > 0
                                ? "Try adjusting your search or filter criteria."
                                : user?.role === 'company_user'
                                    ? "Contact your administrator to request access to customer accounts."
                                    : "Create your customer database to get started with Kranawetter Driver Portal."
                            }
                        </Typography>
                        {user?.role !== 'company_user' && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAdd}
                            >
                                Add First Customer
                            </Button>
                        )}
                    </Paper>
                </Grid>
            )}
        </Grid>
    );

    // Render map view
    const renderMapView = () => {
        // Filter customers that have coordinates
        const customersWithCoordinates = filteredCustomers.filter(
            customer => customer.address && 
            customer.address.coordinates && 
            customer.address.coordinates.length === 2
        );
        
        return (
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Customer Locations Map
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Interactive map showing all customer locations. Click on markers to view details.
                </Typography>
                
                {customersWithCoordinates.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <MapIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No customers with location data
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Add addresses to customers to see them on the map.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ height: { xs: 400, md: 500 }, borderRadius: 2, overflow: 'hidden' }}>
                        <MapComponent 
                            customers={customersWithCoordinates}
                            onMarkerClick={handleMarkerClick}
                        />
                    </Box>
                )}
            </Paper>
        );
    };

    return (
        <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: { xs: 0.5, md: 1 } }}>
            

            {error && (
                <Alert 
                    severity="error" 
                    sx={{ mb: 3, borderRadius: 2 }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert 
                    severity="success" 
                    sx={{ mb: 3, borderRadius: 2 }}
                    onClose={() => setSuccessMessage('')}
                >
                    {successMessage}
                </Alert>
            )}

            {/* Compact Summary Bar */}
            <Paper sx={{ p: 1, mb: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Stack>
                            <Typography variant="caption" color="text.secondary">Customers</Typography>
                            <Chip 
                                label={totalCustomers}
                                sx={{ 
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #e94f37 0%, #f7775e 100%)',
                                    color: 'white',
                                    '& .MuiChip-label': { px: 1.5 }
                                }}
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Stack>
                            <Typography variant="caption" color="text.secondary">With Drop Lots</Typography>
                            <Chip 
                                label={customersWithDropLots}
                                sx={{ 
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                    color: 'white',
                                    '& .MuiChip-label': { px: 1.5 }
                                }}
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Stack>
                            <Typography variant="caption" color="text.secondary">Total Drop Lots</Typography>
                            <Chip 
                                label={totalDropLots}
                                sx={{ 
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
                                    color: 'white',
                                    '& .MuiChip-label': { px: 1.5 }
                                }}
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Controls */}
            <Paper sx={{ p: 1, mb: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Customers ({filteredCustomers.length})
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {user?.role !== 'company_user' && (
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAdd}
                                disabled={refreshing}
                            >
                                Add Customer
                            </Button>
                        )}
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                // Export filtered customers to CSV
                                const rows = filteredCustomers.map(c => ({
                                    Name: c.name,
                                    Contact: c.contactPerson || '',
                                    Phone: c.phone || '',
                                    City: c.address?.city || '',
                                    State: c.address?.state || '',
                                    DropLots: dropLotCounts[c._id] || 0
                                }));
                                const headers = Object.keys(rows[0] || { Name: '', Contact: '', Phone: '', City: '', State: '', DropLots: 0 });
                                const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
                                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', 'customers.csv');
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                            }}
                        >
                            Export CSV
                        </Button>
                        <Button
                            size="small"
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(!showFilters)}
                            variant={showFilters ? "contained" : "outlined"}
                        >
                            Filters
                        </Button>
                    </Stack>
                </Stack>

                <Collapse in={showFilters}>
                    <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        spacing={2} 
                        sx={{ mb: 2 }}
                    >
                        {/* Enhanced search field */}
                        <TextField
                            size="small"
                            placeholder="Search customers..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                            sx={{ flex: 1 }}
                        />
                        
                        {/* Enhanced filter dropdown */}
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by Drop Lots</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Filter by Drop Lots"
                                onChange={(e) => handleStatusFilterChange(e.target.value)}
                            >
                                <MenuItem value="all">All Customers</MenuItem>
                                <MenuItem value="with_drop_lots">With Drop Lots</MenuItem>
                                <MenuItem value="without_drop_lots">Without Drop Lots</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Collapse>

                {/* View Mode Tabs */}
                <Tabs
                    value={viewMode}
                    onChange={handleViewModeChange}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab 
                        icon={<ViewListIcon />} 
                        label={isMobile ? "" : "Table"} 
                        value="table" 
                        iconPosition="start"
                    />
                    <Tab 
                        icon={<MapIcon />} 
                        label={isMobile ? "" : "Map"} 
                        value="map" 
                        iconPosition="start"
                    />
                    <Tab 
                        icon={<ViewModuleIcon />} 
                        label={isMobile ? "" : "Cards"} 
                        value="card" 
                        iconPosition="start"
                    />
                </Tabs>
            </Paper>

            {/* Content */}
            <Box sx={{ mb: 3 }} data-usage-screen={viewMode === 'card' ? 'customers.cards' : viewMode === 'map' ? 'customers.map' : 'customers.table'}>
                {viewMode === 'table' && renderTableView()}
                {viewMode === 'card' && renderCardView()}
                {viewMode === 'map' && renderMapView()}
            </Box>

            {/* Mobile FAB */}
            {isMobile && user?.role !== 'company_user' && (
                <Fab
                    color="primary"
                    disabled={refreshing}
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: 1000
                    }}
                    onClick={handleAdd}
                >
                    <AddIcon />
                </Fab>
            )}

            {/* Render the CustomerForm Dialog */}
            <CustomerForm
                open={openForm}
                onClose={handleCloseForm}
                customer={selectedCustomer}
                onSave={handleSave}
            />

            {/* Customer Detail View with Drop Lots */}
            <CustomerDetailView
                open={detailViewOpen}
                onClose={handleCloseDetailView}
                customer={detailCustomer}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="error" />
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{customerToDelete?.name}</strong>? 
                        This action cannot be undone and will also remove all associated drop lot locations.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button 
                        onClick={handleCloseDeleteDialog} 
                        disabled={deleteLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmDelete} 
                        color="error" 
                        variant="contained"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                    >
                        {deleteLoading ? 'Deleting...' : 'Delete Customer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notes Dialog */}
            <Dialog
                open={notesDialogOpen}
                onClose={handleCloseNotes}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotesIcon color="primary" />
                    Notes for {selectedCustomerNotes?.name}
                </DialogTitle>
                <DialogContent>
                    {selectedCustomerNotes?.notes ? (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                            {selectedCustomerNotes.notes}
                        </Typography>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <NotesIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No notes available
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                This customer doesn't have any notes yet. You can add notes by editing the customer.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={handleCloseNotes}>
                        Close
                    </Button>
                    <Button 
                        onClick={() => {
                            handleCloseNotes();
                            handleEdit(selectedCustomerNotes);
                        }}
                        variant="contained"
                        startIcon={<EditIcon />}
                    >
                        Edit Customer
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default CustomerList; 